// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { addAccount } from "@insite/client-framework/Services/AccountService";
import { API_URL_CURRENT_FRAGMENT, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { updateEnforcementLevel } from "@insite/client-framework/Services/CustomersService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { AccountModel, BillToModel } from "@insite/client-framework/Types/ApiModels";

export type PostalCodeProps = {
    postalCode: string;
};
type HandlerType = ApiHandler<PostalCodeProps & HasOnSuccess & HasOnError<string>, ServiceResult<AccountModel>>;

export const DispatchBeginLoading: HandlerType = props => {
    props.dispatch({
        type: "xCommon/loadingOverlay",
        data: { key: "add", value: "PostalCodeModal" },
    } as any);
};

export const PostalCodeCreateAccount: HandlerType = async props => {
    const account = { isGuest: true } as AccountModel;
    const state = props.getState();
    const session = getSession(state);

    if (session) {
        account.defaultFulfillmentMethod = session.fulfillmentMethod;
        if (session.pickUpWarehouse) {
            account.defaultWarehouseId = session.pickUpWarehouse.id;
        }
    }

    if (!session.userName) {
        // ONLY do if the account is not already set up.
        props.apiResult = await addAccount({ account });
    }
};

export const RequestAccessToken: HandlerType = async props => {
    if (props?.apiResult?.successful) {
        const data = new URLSearchParams();
        data.append("grant_type", "password");
        data.append("username", props.apiResult.result.userName);
        data.append("password", props.apiResult.result.password);
        data.append("scope", "iscapi offline_access");

        const response = await fetch("/identity/connect/token", {
            method: "POST",
            body: data.toString(),
            headers: new Headers({
                "content-type": "application/x-www-form-urlencoded",
                Authorization: `Basic ${btoa("isc:009AC476-B28E-4E33-8BAE-B5F103A142BC")}`,
            }),
        });

        const result = await (response.json() as Promise<{
            readonly error_description: string;
        }>);
        if (!response.ok) {
            props.parameter.onError?.(result.error_description);
            return false;
        }
    } else {
        props.parameter.onError?.((props.apiResult as any).errorMessage);
    }
};
export const loadAndUpdateBillTo: HandlerType = async props => {
    const state = props.getState();
    const session = getSession(state);
    const currentBillToState = getBillToState(state, session.billToId);
    const awaitableLoadBillTo = makeHandlerChainAwaitable(loadBillTo);
    let billTo = currentBillToState?.value ? { ...currentBillToState.value } : ({} as BillToModel);

    if (!currentBillToState.value) {
        await awaitableLoadBillTo({
            billToId: API_URL_CURRENT_FRAGMENT,
            expand: ["validation"],
            onComplete: result => {
                billTo = result.apiResult as any;
            },
        })(props.dispatch, props.getState);
    }
    if (billTo) {
        billTo.postalCode = props.parameter.postalCode;
        await updateEnforcementLevel({
            billTo: {
                ...billTo,
                postalCode: props.parameter.postalCode,
            } as BillToModel,
        });
    }

    window.location.reload();

    props.dispatch({
        type: "xCommon/loadingOverlay",
        data: { key: "clear", value: "PostalCodeModal" },
    } as any);
};
export const chain = [DispatchBeginLoading, PostalCodeCreateAccount, RequestAccessToken, loadAndUpdateBillTo];

const xPostalCode = createHandlerChainRunner(chain, "xPostalCode");
export default xPostalCode;
